import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { deepPurpleMui, greyMui, rydeColors, ScreenDepth } from "@ryde/theme";

export const AreaPolygonsLegendStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "area-polygons-legend",
})`
  position: absolute;
  top: 70px;
  right: 12px /* @noflip */;
  /* Above the map, but below the right drawers so it never overlaps them (mirrors FLOATING_MENU). */
  z-index: ${ScreenDepth.FLOATING_MENU};
  display: flex;
  flex-direction: column;
  max-height: 65%;
  overflow: hidden;
  /* Never spill past the map's edge when the panel is resized narrow — shrink
     to fit, keeping a 12px gutter on both sides (matches the right offset). */
  width: min(260px, calc(100% - 24px));
  padding: 12px 14px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  border: 1px solid ${greyMui._200};

  & .legend-title {
    flex-shrink: 0;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${greyMui._500};
  }

  & .legend-controls {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${greyMui._200};
  }

  /* In-map shape editing — a distinct lavender "editing zone" at the top. */
  & .legend-edit {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
    padding: 12px;
    border: 1px solid ${deepPurpleMui._100};
    border-inline-start: 3px solid ${deepPurpleMui.A700};
    border-radius: 10px;
    background: ${deepPurpleMui._50};
  }

  & .legend-edit-head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  & .legend-edit-name {
    overflow: hidden;
    font-size: 13px;
    font-weight: 700;
    color: ${greyMui._900};
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & .legend-edit-hint {
    font-size: 11px;
    line-height: 1.45;
    color: ${greyMui._700};
  }

  & .legend-edit-actions {
    display: flex;
    gap: 8px;
  }

  & .legend-edit-actions button {
    min-height: 34px;
    padding: 7px 14px;
    border: 1px solid transparent;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition:
      background-color 0.12s ease,
      box-shadow 0.12s ease;

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid ${deepPurpleMui.A700};
      outline-offset: 2px;
    }
  }

  /* Secondary: quiet ghost button so it doesn't compete with Save. */
  & .legend-edit-cancel {
    flex: 0 0 auto;
    background: transparent;
    color: ${greyMui._700};

    &:hover:not(:disabled) {
      background: ${greyMui._100};
      color: ${greyMui._800};
    }
  }

  /* Primary: filled, elevated, takes the lead. */
  & .legend-edit-save {
    flex: 1;
    background: ${deepPurpleMui.A700};
    color: ${rydeColors.white};
    box-shadow: 0 2px 6px rgba(98, 0, 234, 0.35);

    &:hover:not(:disabled) {
      background: ${deepPurpleMui.A400};
      box-shadow: 0 3px 10px rgba(98, 0, 234, 0.45);
    }
  }

  & .legend-search-box {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    border: 1px solid ${greyMui._300};
    border-radius: 6px;
  }

  & .legend-search-box:focus-within {
    border-color: ${deepPurpleMui.A700};
  }

  & .legend-search-icon {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: ${greyMui._500};
  }

  & .legend-search {
    flex: 1 1 auto;
    min-width: 0;
    padding: 6px 0;
    border: none;
    background: none;
    font-size: 13px;
    color: ${greyMui._900};
    outline: none;
  }

  & .legend-search-clear {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border: none;
    background: none;
    color: ${greyMui._500};
    cursor: pointer;
  }

  & .legend-search-clear svg {
    width: 12px;
    height: 12px;
  }

  & .legend-search-clear:hover {
    color: ${greyMui._700};
  }

  & .legend-filter {
    display: flex;
    gap: 4px;
  }

  & .legend-check-all {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${greyMui._700};
    cursor: pointer;
    user-select: none;
  }

  & .legend-check-all input {
    margin: 0;
    cursor: pointer;
  }

  & .legend-filter button {
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 6px;
    border: 1px solid ${greyMui._300};
    border-radius: 6px;
    background-color: ${greyMui._50};
    color: ${greyMui._600};
    font-size: 11px;
    line-height: 1.25;
    text-align: center;
    cursor: pointer;
  }

  & .legend-filter button.is-active {
    background-color: ${deepPurpleMui.A700};
    border-color: ${deepPurpleMui.A700};
    color: #fff;
  }

  & .legend-no-results {
    padding: 8px 2px;
    font-size: 13px;
    color: ${greyMui._500};
  }

  & .legend-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    /* Breathing room so the swatch hover scale is not clipped by the
       scroll container; negative margin keeps items aligned with the title. */
    padding: 2px 4px;
    margin: 0 -4px;
  }

  & .legend-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    font-size: 14px;
    color: ${greyMui._900};
  }

  & .legend-item.is-hidden {
    opacity: 0.5;
  }

  & .legend-item.is-hidden .legend-name {
    text-decoration: line-through;
  }

  & .legend-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & button.legend-name--clickable {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    text-align: start;
    color: inherit;
    cursor: pointer;
  }

  & button.legend-name--clickable:hover {
    text-decoration: underline;
  }

  & button.legend-name--clickable:focus-visible {
    outline: 2px solid ${greyMui._500};
    outline-offset: 1px;
    border-radius: 2px;
  }

  & .legend-item.is-empty .legend-name {
    color: ${greyMui._500};
  }

  & .legend-empty-tag {
    flex-shrink: 0;
    margin-inline-start: auto;
    padding: 1px 6px;
    border: 1px solid ${rydeColors.crisisRed};
    border-radius: 8px;
    background-color: ${rydeColors.redTimeEditBg};
    color: ${rydeColors.crisisRed};
    font-size: 11px;
    white-space: nowrap;
  }
`;

// The per-area color is data-driven (palette-indexed), so it is passed as a
// transient prop rather than an inline style. Rendered as a span for empty
// areas (no polygon) via the `as` prop.
export const LegendSwatchStyled = styled.button<{
  $color?: string;
  $isHidden?: boolean;
  $isEmpty?: boolean;
}>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  padding: 0;
  border-radius: 4px;
  border-width: 1px;
  border-style: ${({ $isEmpty }) => ($isEmpty ? "dashed" : "solid")};
  background-color: ${({ $isEmpty, $isHidden, $color }) =>
    $isEmpty || $isHidden ? "transparent" : $color};
  border-color: ${({ $isEmpty, $color }) => ($isEmpty ? greyMui._400 : $color)};
  cursor: ${({ $isEmpty }) => ($isEmpty ? "default" : "pointer")};
  transition: transform 0.1s ease;

  &:hover {
    transform: ${({ $isEmpty }) => ($isEmpty ? "none" : "scale(1.15)")};
  }

  &:focus-visible {
    outline: 2px solid ${greyMui._500};
    outline-offset: 1px;
  }

  /* Locked while its area is being reshaped in-map: stays shown, not toggleable. */
  &:disabled {
    cursor: not-allowed;
    box-shadow: 0 0 0 2px ${deepPurpleMui.A700};
  }

  &:disabled:hover {
    transform: none;
  }
`;
